import React, { useMemo, useState, useEffect } from 'react';
import type {
  BusinessInfo,
  InvoiceLineItem,
  InvoicePaymentStatus,
  PoGstMode,
  PurchaseOrder,
  PurchasingInvoiceDefaults,
  Supplier,
  SupplierInvoice,
} from '../../types';
import TrashIcon from '../icons/TrashIcon';
import PlusIcon from '../icons/PlusIcon';
import XCircleIcon from '../icons/XCircleIcon';
import { computePoTotals } from '../../lib/nzGst';
import { newInvoiceLine, poItemsToInvoiceLines } from '../../lib/invoiceUtils';
import {
  hasBlockingIssues,
  validateSupplierInvoiceRecord,
} from '../../lib/purchasingInvoiceValidation';
import { generateSupplierInvoicePdf } from '../../lib/supplierInvoicePdf';
import InvoiceLineNumericFields from './InvoiceLineNumericFields';

interface SupplierInvoiceBuilderProps {
  invoice: SupplierInvoice | null;
  suppliers: Supplier[];
  purchaseOrders: PurchaseOrder[];
  prefillPurchaseOrderId?: string;
  businessInfo?: BusinessInfo | null;
  purchasingDefaults?: PurchasingInvoiceDefaults;
  staffId?: number;
  onClose: () => void;
  onSave: (invoice: SupplierInvoice) => void | Promise<void>;
}

function toDateInput(ms?: number): string {
  const d = ms ? new Date(ms) : new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseDateInput(value: string): number {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d).getTime();
}

const SupplierInvoiceBuilder: React.FC<SupplierInvoiceBuilderProps> = ({
  invoice,
  suppliers,
  purchaseOrders,
  prefillPurchaseOrderId,
  businessInfo,
  purchasingDefaults,
  staffId,
  onClose,
  onSave,
}) => {
  const defaultGst = purchasingDefaults?.defaultGstMode ?? 'inclusive';
  const [supplierId, setSupplierId] = useState(suppliers[0]?.id ?? 0);
  const [purchaseOrderId, setPurchaseOrderId] = useState<string>('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(toDateInput());
  const [dueDate, setDueDate] = useState(toDateInput());
  const [hasDueDate, setHasDueDate] = useState(false);
  const [gstMode, setGstMode] = useState<PoGstMode>(defaultGst);
  const [paymentStatus, setPaymentStatus] = useState<InvoicePaymentStatus>('unpaid');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<InvoiceLineItem[]>([newInvoiceLine()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (invoice) {
      setSupplierId(invoice.supplierId);
      setPurchaseOrderId(invoice.purchaseOrderId ?? '');
      setInvoiceNumber(invoice.invoiceNumber);
      setInvoiceDate(toDateInput(invoice.invoiceDate));
      setDueDate(toDateInput(invoice.dueDate));
      setHasDueDate(Boolean(invoice.dueDate));
      setGstMode(invoice.gstMode);
      setPaymentStatus(invoice.paymentStatus);
      setNotes(invoice.notes ?? '');
      setItems(invoice.items.length ? invoice.items : [newInvoiceLine()]);
      return;
    }
    const poId = prefillPurchaseOrderId;
    if (poId) {
      const po = purchaseOrders.find((p) => p.id === poId);
      if (po) {
        setSupplierId(po.supplierId);
        setPurchaseOrderId(po.id);
        setGstMode(po.gstMode ?? defaultGst);
        setItems(poItemsToInvoiceLines(po.items));
      }
    }
  }, [invoice, prefillPurchaseOrderId, purchaseOrders, defaultGst]);

  const supplier = suppliers.find((s) => s.id === supplierId);
  const poShape = useMemo(
    () =>
      items.map((line) => ({
        lineId: line.lineId,
        productName: line.description,
        quantity: line.quantity,
        costPerUnit: line.unitPrice,
        unit: line.unit as 'units',
      })),
    [items],
  );
  const totals = useMemo(() => computePoTotals(poShape, gstMode), [poShape, gstMode]);

  const draftInvoice: SupplierInvoice = {
    id: invoice?.id ?? '',
    supplierId,
    purchaseOrderId: purchaseOrderId || undefined,
    invoiceNumber,
    invoiceDate: parseDateInput(invoiceDate),
    dueDate: hasDueDate ? parseDateInput(dueDate) : undefined,
    items,
    gstMode,
    paymentStatus,
    notes: notes.trim() || undefined,
    dateCreated: invoice?.dateCreated ?? Date.now(),
    recordedBy: staffId,
  };

  const validationIssues = useMemo(
    () => validateSupplierInvoiceRecord(draftInvoice, supplier, businessInfo),
    [draftInvoice, supplier, businessInfo],
  );

  const linkablePos = purchaseOrders.filter(
    (po) => po.supplierId === supplierId && po.status !== 'cancelled',
  );

  const handlePoLink = (poId: string) => {
    setPurchaseOrderId(poId);
    const po = purchaseOrders.find((p) => p.id === poId);
    if (po && !invoice) {
      setGstMode(po.gstMode ?? defaultGst);
      setItems(poItemsToInvoiceLines(po.items));
    }
  };

  const updateLine = (index: number, patch: Partial<InvoiceLineItem>) => {
    setItems((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (hasBlockingIssues(validationIssues)) {
      alert(validationIssues.filter((i) => i.level === 'error').map((i) => i.message).join('\n'));
      return;
    }
    setSaving(true);
    try {
      await onSave(draftInvoice);
    } finally {
      setSaving(false);
    }
  };

  const handlePdf = () => {
    if (!supplier || !businessInfo) return;
    if (hasBlockingIssues(validationIssues)) {
      alert(validationIssues.filter((i) => i.level === 'error').map((i) => i.message).join('\n'));
      return;
    }
    generateSupplierInvoicePdf({ invoice: draftInvoice, supplier, businessInfo });
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" data-soft-keyboard-handled>
      <div className="flex h-full w-full max-w-3xl flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-xl font-bold">
            {invoice ? 'Edit supplier invoice' : 'Record supplier tax invoice'}
          </h2>
          <button type="button" onClick={onClose}>
            <XCircleIcon className="h-7 w-7 text-gray-500" />
          </button>
        </div>
        <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-1 flex-col overflow-hidden">
          <div className="space-y-4 overflow-y-auto px-6 py-4">
            {validationIssues.length > 0 ? (
              <ul className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
                {validationIssues.map((issue, i) => (
                  <li key={i} className={issue.level === 'error' ? 'text-red-800' : 'text-amber-900'}>
                    {issue.message}
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-sm font-medium">Supplier *</label>
                <select
                  value={supplierId}
                  onChange={(e) => setSupplierId(Number(e.target.value))}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                >
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Link to PO (optional)</label>
                <select
                  value={purchaseOrderId}
                  onChange={(e) => handlePoLink(e.target.value)}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                >
                  <option value="">No PO link</option>
                  {linkablePos.map((po) => (
                    <option key={po.id} value={po.id}>
                      {po.id} ({po.status})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Their invoice number *</label>
                <input
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Invoice date *</label>
                <input
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">GST pricing</label>
                <select
                  value={gstMode}
                  onChange={(e) => setGstMode(e.target.value as PoGstMode)}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                >
                  <option value="inclusive">GST inclusive</option>
                  <option value="exclusive">GST exclusive</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Payment status</label>
                <select
                  value={paymentStatus}
                  onChange={(e) => setPaymentStatus(e.target.value as InvoicePaymentStatus)}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                >
                  <option value="unpaid">Unpaid</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
            </div>
            <div className="overflow-hidden rounded-lg border">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-2 text-left">Description</th>
                    <th className="px-2 py-2 text-right">Qty</th>
                    <th className="px-2 py-2 text-right">Unit price</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {items.map((line, index) => (
                    <tr key={line.lineId} className="border-t">
                      <td className="px-2 py-1">
                        <input
                          value={line.description}
                          onChange={(e) => updateLine(index, { description: e.target.value })}
                          data-soft-keyboard-handled
                          className="w-full rounded border px-2 py-1"
                        />
                      </td>
                      <InvoiceLineNumericFields
                        line={line}
                        onUpdate={(patch) => updateLine(index, patch)}
                      />
                      <td className="px-2 py-1">
                        <button type="button" onClick={() => setItems((p) => p.filter((_, i) => i !== index))}>
                          <TrashIcon className="h-4 w-4 text-red-500" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button
                type="button"
                onClick={() => setItems((p) => [...p, newInvoiceLine()])}
                className="flex w-full items-center justify-center gap-1 border-t py-2 text-sm text-pink-600"
              >
                <PlusIcon className="h-4 w-4" /> Add line
              </button>
            </div>
          </div>
          <div className="border-t bg-gray-50 px-6 py-4">
            <div className="mb-3 text-sm">
              <p className="flex justify-between">
                <span>Subtotal (ex. GST)</span>
                <span>${totals.subtotalExGst.toFixed(2)}</span>
              </p>
              <p className="flex justify-between">
                <span>GST (15%)</span>
                <span>${totals.gstAmount.toFixed(2)}</span>
              </p>
              <p className="flex justify-between font-bold">
                <span>Total (inc. GST)</span>
                <span>${totals.totalIncGst.toFixed(2)}</span>
              </p>
            </div>
            <div className="flex justify-between gap-3">
              {invoice?.id && businessInfo && supplier ? (
                <button type="button" onClick={handlePdf} className="rounded-md border px-3 py-2 text-sm">
                  Download PDF
                </button>
              ) : (
                <span />
              )}
              <div className="flex gap-3">
                <button type="button" onClick={onClose} className="rounded-md bg-gray-200 px-4 py-2">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || hasBlockingIssues(validationIssues)}
                  className="rounded-md bg-pink-600 px-4 py-2 text-white disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save invoice'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupplierInvoiceBuilder;
